import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import APIError from 'types/api_error';
import { TrackableList, UpdateTrackableList } from 'types/trackables';

import { useAPI } from 'hooks/api/useAPI';
import { updateQueriesData } from 'services/update_queries_data';

type UseUpdateTrackableListParams = {
  id: string;
  data: UpdateTrackableList;
};

export function useUpdateTrackableList({
  onMutate,
  onError,
  onSettled,
  ...options
}: UseMutationOptions<TrackableList, APIError, UseUpdateTrackableListParams> = {}) {
  const queryClient = useQueryClient();
  const { applicationID, client } = useAPI();

  return useMutation(
    async ({ id, data }: UseUpdateTrackableListParams) => {
      const response = await client.put(`/apps/${applicationID}/trackables/${id}`, data);
      return response.data;
    },
    {
      ...options,
      onMutate: async (params) => {
        onMutate?.(params);

        return updateQueriesData({
          queryClient,
          listQueryKey: ['trackable-lists'],
          singleQueryKey: ['trackable-list'],
          ids: [params.id],
          updateData: (prevData: TrackableList) => ({
            ...prevData,
            ...params.data,
            updated_at: new Date().toISOString(),
          }),
        });
      },
      onError: (error, params, context) => {
        onError?.(error, params, context);

        context?.previousLists?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
        context?.previousSingles?.forEach(([queryKey, data]) =>
          queryClient.setQueryData(queryKey, data)
        );
      },
      onSettled: (...args) => {
        onSettled?.(...args);

        queryClient.invalidateQueries(['trackable-lists']);
        queryClient.invalidateQueries(['trackable-list']);
      },
    }
  );
}